import { useCallback, useState } from "react"

const useAudioPlayer = () => {

  const [audio, setAudio] = useState<HTMLAudioElement | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)

  const audioRef = useCallback((node: HTMLAudioElement | null) => {
    if (node) {
      const handleTimeUpdate = () => {
        setCurrentTime(node.currentTime)
      }

      const handleLoadedMetadata = () => {
        if (isFinite(node.duration)) {
          setDuration(node.duration)
        } else {
          setDuration(0)
        }
      }

      const handleEnded = () => {
        setIsPlaying(false)
      }

      const handleLoadStart = () => {
        setCurrentTime(0)
        setDuration(0)
        setIsPlaying(false)
      }

      node.addEventListener('timeupdate', handleTimeUpdate)
      node.addEventListener('loadedmetadata', handleLoadedMetadata)
      node.addEventListener('ended', handleEnded)
      node.addEventListener('loadstart', handleLoadStart)

      setAudio(node)

      return () => {
        node.removeEventListener('timeupdate', handleTimeUpdate)
        node.removeEventListener('loadedmetadata', handleLoadedMetadata)
        node.removeEventListener('ended', handleEnded)
        node.removeEventListener('loadstart', handleLoadStart)
      }
    }
  }, [])

  const onPlay = useCallback(() => {
    if (!audio) { return }

    audio.play().catch((error) => {
      console.error('Playback failed:', error)
      setIsPlaying(false)
    })
    setIsPlaying(true)
  }, [audio])

  const onPause = useCallback(() => {
    if (!audio) { return }
    
    audio.pause()
    setIsPlaying(false)
  }, [audio])

  const onCancel = useCallback(() => {
    if (!audio) { return }

    audio.pause()
    audio.currentTime = 0
    setCurrentTime(0)
    setIsPlaying(false)
  }, [audio])

  const onSeek = useCallback((time: number) => {
    if (!audio) { return }

    audio.currentTime = time
    setCurrentTime(time)
  }, [audio])

  return [{
    audioRef,
    isPlaying,
    currentTime,
    duration,
    onPlay,
    onPause,
    onCancel,
    onSeek,
  }]
}

export default useAudioPlayer